import * as React from "react";
import {useEffect, useState, useMemo, useCallback} from "react";
import {Map} from "immutable";
import {ProductSummary} from "../../model/product/ProductSummary";
import {getSuccessData, REQUEST_STATES, RequestState} from "../common/commonStates";
import {
    currentPlan,
    hasAllAccessEnabled,
    hasClientPortfolioAccess,
    hasClientResearchEnabled,
    hasDynamicResearchAccess,
    hasPortfolioAccess,
    hasResearchAccess,
    isEditingPlanPortfolio,
    planCountryName,
    portfolioProductSummariesSelector
} from "../../../mainReducerMapSelectors";
import {IApplicationRootState} from "../../../applicationState";
import {connect, useDispatch} from "react-redux";
import {FormattedMessage, injectIntl, IntlShape} from "react-intl";
import researchPageActions from "./ResearchPageActions";
import {getTranslation} from "../../utils/translationUtil";
import {hasConsultantLevelAccess, hasResearchPageAccess} from "../../utils/sessionUtil";
import {RaisedButton} from "../common/RaisedButton";
import {defaultButtonStyle, prominentButtonStyle} from "../common/buttonStyles";
import {AdminActionTypes} from "./edit-portfolio/editPortfolioActions";
import {IPlanInfo} from "../../model/ClientsAndPlans.model";
import productActions from "../product/productActions";
import {NoAccessNotification} from "../base/header/NoAccessNotification";
import {ErrorComponent} from "../base/Error.component";
import {bindActionCreators, Dispatch} from "redux";
import {byRatingAndManagerNameAndProductName, fixBlankAndTranslate, mapProductForDisplay} from "../../utils/researchUtil";
import {SliderComponent} from "../common/Slider.component";
import {RatingDefinitions} from "./RatingDefinitions";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {AnyThunkDispatch} from "../../../commonTypes";
import {useResearchFilters} from "./useResearchFilters";
import {useStickyControls} from "../../utils/useStickyControls";
import {ResearchFilterBar} from "./ResearchFilterBar.component";
import {ResearchPagination} from "./ResearchPagination.component";
import {RESEARCH_TABLE_COLUMNS, ResearchTable} from "./ResearchTable.component";
import {DisplayProductSummary} from "../../model/ResearchPageState.model";
import ResearchSearch from "./ResearchSearch.component";
import {getPortfolioProductSummaries} from "../product/AllProductsActions";

export interface IResearchPropsFromStore {
    portfolioProductSummaries: Map<number, ProductSummary> | null;
    portfolioProductSummariesState: RequestState<Map<number, ProductSummary>>;
    hasDynamicResearchAccess: boolean;
    hasAllAccessEnabled: boolean;
    planCountryName: string;
    currentPlan: IPlanInfo | undefined;
    hasClientResearchEnabled: boolean;
    hasResearchAccess: boolean;
    hasPortfolioAccess: boolean;
    clientPortfolioEnabled: boolean;
    editingPlanPortfolio: boolean;
}

export interface IResearchPropsFromActions {
    actions: {
        removeProductFromPlanPortfolio: (backstopId: number) => void;
    };
}

export interface IResearchIntlProps {
    intl: IntlShape;
}

export type IResearchProps = IResearchPropsFromStore & IResearchPropsFromActions & IResearchIntlProps;

export const CLIENT_USER_HIDDEN_MESSAGE = "The Research section is currently hidden from client users of this plan.";
export const REMOVE_STRATEGIES_MESSAGE = "To remove strategies, click the X in the remove column.";

export const ResearchPage: React.FunctionComponent<IResearchProps> = (props) => {
    const [displaySlider, setDisplaySlider] = useState<boolean>(false);
    const dispatch: AnyThunkDispatch = useDispatch();

    useEffect(() => {
        const shouldRequestSummaries = props.portfolioProductSummariesState.kind === REQUEST_STATES.NOT_REQUESTED
            || props.portfolioProductSummariesState.kind === REQUEST_STATES.REQUEST_FAILED;
        if (shouldRequestSummaries) {
            dispatch(getPortfolioProductSummaries);
        }
    }, [dispatch, props.portfolioProductSummaries, props.portfolioProductSummariesState.kind]);

    const allData: DisplayProductSummary[] = useMemo(() => {
        if (!props.portfolioProductSummaries) return [];
        return props.portfolioProductSummaries
            .valueSeq()
            .toArray()
            .map((it) => fixBlankAndTranslate(it) as DisplayProductSummary)
            .map(mapProductForDisplay)
            .sort(byRatingAndManagerNameAndProductName);
    }, [props.portfolioProductSummaries]);

    const filters = useResearchFilters(allData);

    const handleNavigateToManager = useCallback((managerBackstopId: number) => {
        dispatch(researchPageActions.setScrollToBackstopAction(managerBackstopId));
    }, [dispatch]);

    const handleNavigateToProduct = useCallback((backstopId: number) => {
        dispatch(researchPageActions.setScrollToBackstopAction(backstopId));
    }, [dispatch]);

    const handleRemoveProduct = useCallback((backstopId: number) => {
        props.actions.removeProductFromPlanPortfolio(backstopId);
    }, [props.actions]);

    const {containerRef: scrollAreaRef, stickyControlsRef} = useStickyControls(
        [filters.facetedFilters, filters.searchText],
    );

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [filters.pagination.pageIndex]);

    const renderEditControlButton = () => {
        if (!hasConsultantLevelAccess() || props.hasDynamicResearchAccess
            || props.hasAllAccessEnabled || props.planCountryName === "UK") {
            return null;
        }

        return props.editingPlanPortfolio ? renderDoneEditingButton() : renderEditPortfolioButton();
    };

    const renderEditPortfolioButton = () => {
        return (
            <RaisedButton
                className="research-page__edit-portfolio-button"
                style={defaultButtonStyle}
                onClick={() => dispatch({type: AdminActionTypes.BEGIN_EDIT_PORTFOLIO})}
                primary={true}
            >
                <FormattedMessage id="research.edit-portfolio" defaultMessage="Edit Portfolio" />
            </RaisedButton>
        );
    };

    const renderDoneEditingButton = () => {
        return (
            <RaisedButton
                className="research-page__done-portfolio-button"
                style={prominentButtonStyle}
                onClick={() => dispatch({type: AdminActionTypes.END_EDIT_PORTFOLIO})}
                primary={true}
            >
                <FormattedMessage id="research.done-editing" defaultMessage="Done Editing" />
            </RaisedButton>
        );
    };

    const renderNoResearchNotification = () => {
        return !props.hasClientResearchEnabled
            ? <NoAccessNotification
                message={getTranslation("research.hidden-from-clients", CLIENT_USER_HIDDEN_MESSAGE)} />
            : null;
    };

    const renderEditingHeader = () => {
        if (!props.editingPlanPortfolio) return null;
        return (
            <div className="research-page__editing-header" data-testid="research-page__editing-header">
                <div className="research-page__editing-header-content">
                    <h1>{getTranslation("research-page.editing-portfolio", "Editing Client Portfolio")}: {props.currentPlan?.name}</h1>
                    <div className="research-page__help-text">
                        <div>
                            <FormattedMessage
                                id="research.remove-strategies-help"
                                defaultMessage={REMOVE_STRATEGIES_MESSAGE}
                            />
                        </div>
                    </div>
                    <ResearchSearch />
                </div>
                <div className="research-page__editing-header-actions">
                    {renderDoneEditingButton()}
                </div>
            </div>
        );
    };

    if (!hasResearchPageAccess(props.hasResearchAccess, props.hasPortfolioAccess, props.clientPortfolioEnabled)) {
        return <ErrorComponent />;
    }

    if (!props.portfolioProductSummaries) {
        return <LoadingSpinner />;
    }

    return (
        <div className="research-page" data-testid="research-page">
            <SliderComponent
                hideSlider={() => setDisplaySlider(false)}
                header={getTranslation("research-page.rating.definitions", "Rating Definitions")}
                content={<RatingDefinitions />}
                display={displaySlider}
            />

            <div className="research-page__scroll-area" ref={scrollAreaRef}>
                {renderNoResearchNotification()}
                {!props.editingPlanPortfolio && (
                    <div className="research-page__header" role="banner">
                        <div className="research-page__header-inner">
                            <div className="research-page__header-content">
                                <h1 className="research-page__header-title">
                                    {getTranslation("subnav.researchItem", "Research")}
                                </h1>
                                <p className="research-page__header-description">
                                    {getTranslation(
                                        "research-page.research.description",
                                        "Explore Aon\u2019s comprehensive views on investment managers and strategies."
                                    )}
                                </p>
                            </div>
                            <div className="research-page__header-actions">
                                {renderEditControlButton()}
                            </div>
                        </div>
                    </div>
                )}

                <div className="research-page__scroll-content">
                    {renderEditingHeader()}

                    <div className="research-page__sticky-controls" ref={stickyControlsRef}>
                        <ResearchFilterBar
                            filters={filters}
                            filteredData={filters.filteredData}
                            onOpenRatingDefinitions={() => setDisplaySlider(true)}
                        />
                    </div>

                    <ResearchTable
                        columns={RESEARCH_TABLE_COLUMNS}
                        data={filters.paginatedData}
                        searchText={filters.searchText}
                        totalFiltered={filters.totalFiltered}
                        onNavigateToProduct={handleNavigateToProduct}
                        onNavigateToManager={handleNavigateToManager}
                        editMode={props.editingPlanPortfolio}
                        onRemoveProduct={handleRemoveProduct}
                    />
                </div>
            </div>

            <ResearchPagination
                pagination={filters.pagination}
                totalItems={filters.totalFiltered}
                onPaginationChange={filters.setPagination}
            />
        </div>
    );
};

export const mapStateToProps = (state: IApplicationRootState): IResearchPropsFromStore => {
    return {
        portfolioProductSummaries: getSuccessData(portfolioProductSummariesSelector(state)),
        portfolioProductSummariesState: portfolioProductSummariesSelector(state)!,
        hasDynamicResearchAccess: hasDynamicResearchAccess(state),
        hasAllAccessEnabled: hasAllAccessEnabled(state),
        planCountryName: planCountryName(state),
        currentPlan: currentPlan(state),
        editingPlanPortfolio: isEditingPlanPortfolio(state),
        hasClientResearchEnabled: hasClientResearchEnabled(state),
        hasResearchAccess: hasResearchAccess(state),
        hasPortfolioAccess: hasPortfolioAccess(state),
        clientPortfolioEnabled: hasClientPortfolioAccess(state)
    };
};

export const mapDispatchToProps = (dispatch: Dispatch): IResearchPropsFromActions => {
    return {
        actions: bindActionCreators({
            removeProductFromPlanPortfolio: productActions.removeProductFromPlanPortfolio,
        }, dispatch),
    };
};

const connectedResearchPage = connect<IResearchPropsFromStore, IResearchPropsFromActions>(
    mapStateToProps,
    mapDispatchToProps
)(ResearchPage);

export default injectIntl(connectedResearchPage);
